import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import DashboardLayout from './DashboardLayout';
import styles from './CEODashboard.module.css';
import { 
  FiBarChart2, FiTrendingUp, FiUsers, FiTarget,
  FiCalendar, FiDollarSign, FiArrowUp, FiArrowDown,
  FiCheckCircle, FiAlertCircle, FiDownload, FiPieChart,
  FiClock, FiMessageSquare, FiAward
} from 'react-icons/fi';

const CEODashboard = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tasksReceived, setTasksReceived] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [okrs, setOkrs] = useState([]);
  const [departmentPerformance, setDepartmentPerformance] = useState([]);
  const [pendingDecisions, setPendingDecisions] = useState([]);

  useEffect(() => {
    fetchUser();
    fetchDashboardData();
  }, []);

  const fetchUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      setUser(profile);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch tasks assigned to CEO
      if (user?.id) {
        const { data: tasks } = await supabase
          .from('tasks')
          .select('*, assigned_by_user:assigned_by(name)')
          .eq('assigned_to', user.id)
          .order('created_at', { ascending: false });
        setTasksReceived(tasks || []);
      }

      // Mock revenue data
      setRevenueData([
        { month: 'Jan', revenue: 4.2, target: 4.0 },
        { month: 'Feb', revenue: 4.5, target: 4.2 },
        { month: 'Mar', revenue: 4.8, target: 4.5 },
        { month: 'Apr', revenue: 5.1, target: 4.8 },
        { month: 'May', revenue: 5.4, target: 5.0 },
        { month: 'Jun', revenue: 5.8, target: 5.3 }
      ]);

      setOkrs([
        { objective: 'Grow Market Leadership', progress: 65, target: 'Q4 2024' },
        { objective: 'Drive Innovation Excellence', progress: 80, target: 'Q3 2024' },
        { objective: 'Enhance Customer Experience', progress: 72, target: 'Q4 2024' },
        { objective: 'Operational Efficiency', progress: 58, target: 'Q1 2025' }
      ]);

      setDepartmentPerformance([
        { department: 'Sales', kpi: 'Revenue', achievement: 92, trend: 'up' },
        { department: 'Marketing', kpi: 'Lead Generation', achievement: 88, trend: 'up' },
        { department: 'Product', kpi: 'Feature Delivery', achievement: 78, trend: 'down' },
        { department: 'Customer Success', kpi: 'NPS Score', achievement: 85, trend: 'up' },
        { department: 'Engineering', kpi: 'Sprint Completion', achievement: 82, trend: 'up' }
      ]);

      setPendingDecisions([
        { title: 'New Office Expansion', priority: 'high', deadline: 'Oct 20, 2024' },
        { title: 'Strategic Partnership with Tech Giant', priority: 'critical', deadline: 'Oct 15, 2024' },
        { title: 'Budget Allocation Q4', priority: 'high', deadline: 'Oct 25, 2024' },
        { title: 'Product Launch Strategy', priority: 'medium', deadline: 'Nov 5, 2024' }
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await supabase
        .from('tasks')
        .update({ status: newStatus, updated_at: new Date() })
        .eq('id', taskId);
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="CEO">
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="CEO">
      <div className={styles.ceoDashboard}>
        <div className={styles.header}>
          <div>
            <h1>CEO Dashboard</h1>
            <p>Executive Performance & Strategic Planning</p>
          </div>
          <button className={styles.exportBtn}>
            <FiDownload /> Export Report
          </button>
        </div>

        {/* Tasks from MD */}
        <div className={styles.sectionCard}>
          <h2><FiMessageSquare /> Tasks from Managing Director</h2>
          <div className={styles.tasksList}>
            {tasksReceived.length === 0 ? (
              <p className={styles.noData}>No pending tasks</p>
            ) : (
              tasksReceived.map(task => (
                <div key={task.id} className={styles.taskCard}>
                  <div className={styles.taskHeader}>
                    <div>
                      <h3>{task.title}</h3>
                      <p>{task.description}</p>
                    </div>
                    <div className={styles.taskActions}>
                      <select 
                        value={task.status} 
                        onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                        className={styles.statusSelect}
                      >
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                  <div className={styles.taskMeta}>
                    <span>From: {task.assigned_by_user?.name || 'MD'}</span>
                    <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                    <span className={`${styles.priorityBadge} ${styles[task.priority]}`}>
                      {task.priority}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Revenue Chart */}
        <div className={styles.sectionCard}>
          <h2><FiDollarSign /> Revenue Performance</h2>
          <div className={styles.revenueChart}>
            <div className={styles.chartBars}>
              {revenueData.map((data, index) => (
                <div key={index} className={styles.chartBar}>
                  <div className={styles.barLabels}>
                    <span>{data.month}</span>
                    <span>₹{data.revenue}M</span>
                  </div>
                  <div className={styles.barContainer}>
                    <div className={styles.revenueBar} style={{ width: `${(data.revenue / 6) * 100}%` }}></div>
                    <div className={styles.targetLine} style={{ left: `${(data.target / 6) * 100}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.chartLegend}>
              <span><div className={styles.revenueIndicator}></div> Actual Revenue</span>
              <span><div className={styles.targetIndicator}></div> Target</span>
            </div>
          </div>
        </div>

        {/* OKRs Section */}
        <div className={styles.twoColumnGrid}>
          <div className={styles.sectionCard}>
            <h2><FiTarget /> Company OKRs</h2>
            <div className={styles.okrsList}>
              {okrs.map((okr, index) => (
                <div key={index} className={styles.okrCard}>
                  <div className={styles.okrHeader}>
                    <h3>{okr.objective}</h3>
                    <span className={styles.okrTarget}>{okr.target}</span>
                  </div>
                  <div className={styles.progressBar}>
                    <div className={styles.progress} style={{ width: `${okr.progress}%` }}></div>
                  </div>
                  <div className={styles.progressText}>{okr.progress}% Complete</div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.sectionCard}>
            <h2><FiAlertCircle /> Strategic Decisions Required</h2>
            <div className={styles.decisionsList}>
              {pendingDecisions.map((decision, index) => (
                <div key={index} className={styles.decisionItem}>
                  <div className={`${styles.priorityBadge} ${styles[decision.priority]}`}>
                    {decision.priority}
                  </div>
                  <div className={styles.decisionInfo}>
                    <p>{decision.title}</p>
                    <small>Due: {decision.deadline}</small>
                  </div>
                  <button className={styles.reviewBtn}>Review</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Department Performance */}
        <div className={styles.sectionCard}>
          <h2><FiUsers /> Department Performance</h2>
          <div className={styles.deptList}>
            {departmentPerformance.map((dept, index) => (
              <div key={index} className={styles.deptItem}>
                <div className={styles.deptInfo}>
                  <h4>{dept.department}</h4>
                  <span>{dept.kpi}</span>
                </div>
                <div className={styles.deptProgress}>
                  <div className={styles.progressBar}>
                    <div className={styles.progress} style={{ width: `${dept.achievement}%` }}></div>
                  </div>
                  <span className={styles.achievement}>{dept.achievement}%</span>
                </div>
                <div className={dept.trend === 'up' ? styles.trendUp : styles.trendDown}>
                  {dept.trend === 'up' ? <FiArrowUp /> : <FiArrowDown />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CEODashboard;